/**
 * ModuleNotEnabled — Shown when a user navigates to a gated route
 * without the required entitlement.
 *
 * - All users see the "Module not enabled" message.
 * - Admin users (owner/admin) additionally see a "Contact support to enable" CTA.
 * - Non-admin users do NOT see the CTA (least-privilege).
 */

import { Link } from '@tanstack/react-router';
import { useIsAdmin } from '@/hooks';

interface ModuleNotEnabledProps {
  moduleName: string;
}

export function ModuleNotEnabled({ moduleName }: ModuleNotEnabledProps) {
  const isAdmin = useIsAdmin();

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center max-w-md mx-auto">
        {/* Icon */}
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted/10">
          <svg
            className="h-8 w-8 text-muted"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"
            />
          </svg>
        </div>

        {/* Heading */}
        <h1 className="mt-6 text-xl font-semibold text-text">Module not enabled</h1>

        {/* Description */}
        <p className="mt-2 text-sm text-muted">
          The <strong>{moduleName}</strong> module is not included in your current plan.
        </p>

        {/* Admin-only CTA */}
        {isAdmin && (
          <div className="mt-6">
            <a
              href="mailto:support@11to1.com?subject=Enable%20module%20request"
              className="btn-primary inline-flex items-center gap-2"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                />
              </svg>
              Contact support to enable
            </a>
          </div>
        )}

        <div className="mt-4">
          <Link
            to="/settings"
            className="text-sm text-primary hover:text-primary/80 transition-colors duration-fast"
          >
            Back to Settings
          </Link>
        </div>
      </div>
    </div>
  );
}
